import React, { useCallback, useEffect } from 'react'
import * as Yup from 'yup'
import { useIntl } from 'react-intl'
import { Form, Formik } from 'formik'
import { useDispatch } from 'react-redux'
import effects from 'app/effects/effects'
import { useMutation } from '@tanstack/react-query'
import { makeStyles, Typography } from '@material-ui/core'

import { useError } from 'commons/useError'
import { SECONDARY } from 'app/constants/button'
import { disableSpaceBar } from 'commons/keyDown'
import { emailRegExpFormat } from 'commons/email'
import MessageAlert from 'app/components/MessageAlert'
import { useHealthCheck } from 'commons/useHealthCheck'

import LabelInput from 'app/componentV4/Labels/LabelInput'
import SimpleInput from 'app/componentV4/Input/SimpleInput'
import ContainedButton from 'app/componentV4/Button/ContainedButton'
import PanelsIllustrationMSG from 'app/componentV4/Panels/IllustrationMSG'

const useStyles = makeStyles({
  input: {
    '&.MuiOutlinedInput-root': {
      height: '48px !important',
    },
  },
})

function ForgotPassword() {
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const dispatch = useDispatch()
  const { errors } = useError()

  const health = useHealthCheck()

  const requestForgotPassword = useMutation({
    mutationFn: async (value) => {
      const response = await effects.apis.authV2.forgotPassword({
        email: value.email,
      })
      return response
    },
    onError: (error) => {
      errors(formatMessage({ id: `error.${error.response.data.error.name}` }))
    },
  })

  const handleAhealtCheck = useCallback(async () => {
    await health.healtCheck.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    handleAhealtCheck()
  }, [handleAhealtCheck])

  return (
    <PanelsIllustrationMSG>
      <div className="flex justify-center items-center h-full">
        <div className="w-full">
          {/* Header */}
          <Typography className="text-18 font-500 text-primary text-center">
            {'Forget password'}
          </Typography>

          <div className="items-center my-10">
            <Typography className="text-16 text-primary-300 text-center mx-5">
              {
                'Enter your email address associated with your account, we will send you a link to reset your password.'
              }
            </Typography>
          </div>

          {/* Form Email Address */}
          <Formik
            initialValues={{
              email: '',
            }}
            validationSchema={Yup.object({
              email: Yup.string()
                .matches(emailRegExpFormat, 'Invalid email address format')
                .required('This field is required'),
            })}
            onSubmit={async (values, { resetForm }) => {
              await requestForgotPassword.mutate(values, {
                onSuccess: () => {
                  dispatch(
                    MessageAlert.showSuccess({
                      message: formatMessage({ id: 'forgot.successMessage' }),
                    }),
                  )
                  resetForm()
                },
              })
            }}
          >
            {({ values, errors, touched, handleBlur, handleChange }) => (
              <Form>
                <LabelInput label={'Email Address'} required>
                  <SimpleInput
                    className={classes.input}
                    id="login-input-email"
                    name="email"
                    placeholder={'Email Address'}
                    value={values.email}
                    size="medium"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    inputProps={{
                      'aria-label': 'Email Forget Password',
                      onKeyDown: (e) => {
                        disableSpaceBar(e)
                      },
                    }}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                  />
                </LabelInput>

                {/* Continue button */}
                <div className="flex justify-center mt-10">
                  <ContainedButton
                    type="submit"
                    color={SECONDARY}
                    className="w-full h-48"
                    isLoading={requestForgotPassword.isPending}
                    disabled={requestForgotPassword.isPending}
                  >
                    {'Send'}
                  </ContainedButton>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </PanelsIllustrationMSG>
  )
}

export default ForgotPassword
